import axios from "axios";

import {
  UPDATE_STUDENTANSWER,
  FETCH_STUDENTANSWER,
  FETCH_CLASSROOMSTUDENTANSWER,
  DELETE_STUDENTANSWER
} from "./type";

import { initApollo } from "../graphql/init";
import {
  RECORD_WRONG_ANSWERS_BATCH,
  RECORD_CORRECT_ANSWERS_BATCH
} from "../graphql/mutations/RecordWrongAnswer";
import { savePendingUpload } from "../utils/audioUploadPersistence";

// 處理音檔上傳的輔助函數
// userId is REQUIRED for the IDB retry path — without it the cross-session
// sweep can't locate the failed upload (getPendingUploads filters strictly
// by metadata.userId, otherwise entries from another user could leak).
const uploadAudioFile = async (audio, userId) => {
  if (!audio?.file) return audio; // 如果音檔不存在，直接返回原資料

  const fd = new FormData();
  // Determine file extension based on blob type
  const fileExtension = audio.file.type.includes("wav") ? "wav" : "mp4";
  fd.append("audio", audio.file, `audio.${fileExtension}`); // 把音檔加入表單
  fd.append("name", audio.name); // 將音檔名稱加入表單

  // Pass previousAudioId if available for concatenation
  if (audio.previousAudioId) {
    fd.append("previousAudioId", audio.previousAudioId);
  }

  try {
    // No manual Content-Type for FormData — axios 1.x can drop the multipart
    // boundary when a Content-Type header is already provided, breaking multer
    // parsing on the server. Letting XHR set the header keeps the boundary
    // intact in both axios 0.x and 1.x.
    const {
      data: { audio: uploadedAudio }
    } = await axios.post("student/audio", fd, { timeout: 30000 });

    // Remove the file property and previousAudioId to avoid serialization issues
    const { file, previousAudioId, ...audioWithoutFile } = audio;

    return {
      ...audioWithoutFile, // 保留原有資料（除了file）
      _audio: uploadedAudio // 更新音檔資訊
    };
  } catch (error) {
    console.error("音檔上傳失敗", error);
    // Save the blob for cross-session retry. userId MUST be included or the
    // retry sweep can't claim ownership and the entry sits forever.
    try {
      const retryId = `auto-${audio.name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      await savePendingUpload(retryId, audio.file, {
        userId: userId ? String(userId) : "",
        fileName: `audio.${fileExtension}`,
        name: audio.name,
        styleType: "reading",
        previousAudioId: audio.previousAudioId
      });
    } catch (e) {
      // IDB unavailable — nothing more we can do here.
    }
    throw error;
  }
};

// 處理頁面的輔助函數
const processPage = async (page, userId) => {
  // console.log("處理頁面:", page);

  let updatedAnswers = page.answers;

  if (page.audioBlob) {
    // Handle partial progress audio upload
    const uploadedAudio = await uploadAudioFile(
      {
        file: page.audioBlob,
        name: `progress_${page._page}_${Date.now()}`,
        previousAudioId: page.previousAudioId
      },
      userId
    );

    updatedAnswers = {
      ...updatedAnswers,
      audios: [
        {
          _audio: uploadedAudio._audio,
          src: uploadedAudio._audio?.src,
          userText: page.recognitionText,
          completionStatus: "partial"
        }
      ]
    };
  } else if (page.answers?.audios) {
    const updatedAudios = await Promise.all(
      page.answers.audios.map((audio) => {
        // Skip null audios (not yet recorded)
        if (!audio) return null;
        // Skip upload if no file blob — audio was already uploaded by useReadingSubmit.
        // Calling uploadAudioFile without a file would create a corrupt duplicate upload.
        if (!audio.file) return audio;
        return uploadAudioFile(audio, userId);
      })
    );

    updatedAnswers = {
      ...page.answers,
      audios: updatedAudios
    };
  }

  // 返回更新後的頁面，保留 question 和 resumeData
  return {
    ...page,
    question: page.question,
    resumeData: page.resumeData,
    answers: updatedAnswers
  };
};

// Helper function to calculate skill statistics for a section
const calculateSkillStatistics = (sectionData) => {
  const skillsMap = new Map();

  sectionData.forEach((page, index) => {
    const pageSkills = page.skills || [];
    const isCorrect = page.answers?.isCorrect;

    // Only process if answered (isCorrect is not null/undefined)
    if (isCorrect !== null && isCorrect !== undefined) {
      pageSkills.forEach((skill) => {
        if (!skillsMap.has(skill)) {
          skillsMap.set(skill, { totalPages: 0, totalCorrect: 0 });
        }
        const skillData = skillsMap.get(skill);
        skillData.totalPages++;
        if (isCorrect === true) {
          skillData.totalCorrect++;
        }
      });
    }
  });

  const result = Array.from(skillsMap.entries()).map(([skill, data]) => {
    const correctPercentage =
      data.totalPages > 0
        ? parseFloat(((data.totalCorrect / data.totalPages) * 100).toFixed(2))
        : 0;

    return {
      skill,
      totalPages: data.totalPages,
      totalCorrect: data.totalCorrect,
      correctPercentage
    };
  });

  return result;
};

// 主 Action 函數
export const updateStudentAnswer =
  (fromProps, callback) => async (dispatch, getStore) => {
    const { studentAnswerInit } = fromProps;

    console.log("[updateStudentAnswer DEBUG] Called with studentAnswerInit", {
      hasStudentAnswerInit: !!studentAnswerInit,
      sectionsCount: studentAnswerInit?.sections?.length,
      studentAnswerInit
    });

    // Guard: Ensure sections array exists and has at least one section
    if (
      !studentAnswerInit ||
      !studentAnswerInit.sections ||
      studentAnswerInit.sections.length === 0
    ) {
      console.log(
        "[updateStudentAnswer] No sections to save, skipping API call"
      );
      return;
    }

    const unitId = studentAnswerInit._unit;
    const userId = studentAnswerInit._user;

    const sectionId = studentAnswerInit.sections[0]._section;

    console.log("[updateStudentAnswer DEBUG] Extracted IDs", {
      unitId,
      userId,
      sectionId,
      pagesCount: studentAnswerInit.sections[0]?.data?.length
    });

    let currentStudentAnswer;

    try {
      const response = await axios.get(`studentAnswer/${unitId}/${userId}`); //查找是否有這個使用者的unit

      // 如果 response.data 存在，從中解構 currentStudentAnswer，否則設置為 null
      currentStudentAnswer = response.data.studentAnswer || null;

      // Audio lifecycle is managed server-side via _user+_page tags.
      // Do not delete audio docs here — the server merges fragments automatically.

      // 處理每一個頁面
      const ownerUserId = studentAnswerInit._user;
      console.log("[updateStudentAnswer DEBUG] Processing pages", {
        pagesCount: studentAnswerInit.sections[0].data.length,
        pages: studentAnswerInit.sections[0].data.map(p => ({
          pageId: p._page,
          style: p.style,
          styleType: p.styleType,
          hasAnswers: !!p.answers,
          audioCount: p.answers?.audios?.length,
          audiosInfo: p.answers?.audios?.map((a, i) => ({
            index: i,
            hasAudio: !!a,
            hasFile: !!a?.file,
            hasUserText: !!a?.userText,
            score: a?.score
          }))
        }))
      });

      const updatedPages = await Promise.all(
        studentAnswerInit.sections[0].data.map((page) =>
          processPage(page, ownerUserId)
        )
      );

      console.log("[updateStudentAnswer DEBUG] Pages processed", {
        updatedPagesCount: updatedPages.length,
        updatedPages: updatedPages.map(p => ({
          pageId: p._page,
          style: p.style,
          styleType: p.styleType,
          hasAnswers: !!p.answers,
          audioCount: p.answers?.audios?.length,
          audiosInfo: p.answers?.audios?.map((a, i) => ({
            index: i,
            hasAudio: !!a,
            hasAudioObject: !!a?._audio,
            audioSrc: a?._audio?.src,
            audioId: a?._audio?._id || a?._audio?.id,
            hasUserText: !!a?.userText,
            score: a?.score
          }))
        }))
      });

      // Filter out any pages where isVideoCompleted is false
      // Once a video is completed (isVideoCompleted: true), we should never send false
      // This prevents accidentally overwriting true with false
      const filteredPages = updatedPages.filter((page) => {
        // Keep all non-video pages
        if (page.isVideoCompleted === undefined) {
          return true;
        }
        // Only keep video pages where isVideoCompleted is true
        // Skip pages where isVideoCompleted is false or null
        return page.isVideoCompleted === true;
      });

      console.log("[updateStudentAnswer DEBUG] Pages filtered", {
        filteredPagesCount: filteredPages.length,
        removedCount: updatedPages.length - filteredPages.length
      });

      // 計算技能統計
      const skillStatistics = calculateSkillStatistics(filteredPages);

      // 構建更新後的 studentAnswerInit
      const updatedStudentAnswerInit = {
        ...studentAnswerInit,
        sections: [
          {
            ...studentAnswerInit.sections[0],
            skillStatistics,
            data: filteredPages
          }
        ]
      };

      // console.log("更新後的 studentAnswerInit", updatedStudentAnswerInit);

      // Check if there are any file blobs still present
      if (updatedStudentAnswerInit.sections[0]?.data[0]?.answers?.audios) {
        // console.log("音檔資料:", updatedStudentAnswerInit.sections[0].data[0].answers.audios);
        const hasBlob =
          updatedStudentAnswerInit.sections[0].data[0].answers.audios.some(
            (audio) => audio && audio.file instanceof Blob
          );
        if (hasBlob) {
          console.error("警告: 資料中仍包含 Blob 物件，這會導致序列化失敗！");
        }
      }

      console.log("[updateStudentAnswer DEBUG] Sending PATCH request", {
        unitId,
        userId,
        sectionId,
        pagesCount: updatedStudentAnswerInit.sections[0].data.length,
        updatedStudentAnswerInit
      });

      // 發送更新的 API 請求
      const {
        data: { studentAnswer }
      } = await axios.patch(`studentAnswer`, {
        patch: { data: updatedStudentAnswerInit } // 傳遞處理過的資料
      });

      console.log("[updateStudentAnswer DEBUG] PATCH response received", {
        studentAnswerId: studentAnswer._id,
        sectionsCount: studentAnswer.sections?.length,
        pagesInSection: studentAnswer.sections?.[0]?.data?.length,
        studentAnswer
      });

      // 如果有需要，可以 dispatch action 更新 Redux store
      dispatch({ type: UPDATE_STUDENTANSWER, payload: { studentAnswer } });

      if (callback) callback();
    } catch (error) {
      console.error("更新學生答案失敗", error);
      // Re-throw so callers that opt-in with .catch() can persist the blob
      // for retry / show a banner. Callers that don't .catch() will see an
      // unhandled-rejection console warning, no functional change vs before.
      throw error;
    }
  };

// fetch current student studentAnswer
export const fetchStudentAnswer =
  (options, callback) => async (dispatch, getStore) => {
    const { booksetId, bookTypeId, bookId, unitId, productId } = options;

    // 使用 URLSearchParams 來處理查詢參數
    const queryParams = new URLSearchParams();

    if (booksetId) queryParams.append("booksetId", booksetId);
    if (bookTypeId) queryParams.append("bookTypeId", bookTypeId);
    if (bookId) queryParams.append("bookId", bookId);
    if (unitId) queryParams.append("unitId", unitId);
    if (productId) queryParams.append("productId", productId);

    // 構建查詢字串
    const query = queryParams.toString() ? `?${queryParams.toString()}` : "";

    try {
      // 發送請求到後端來獲取對應的資料
      const response = await axios.get(`studentAnswer${query}`);

      // 從 response.data 解構 studentAnswer
      const { studentAnswers } = response.data;

      // 假設你有一個 action type 來處理返回的數據
      dispatch({ type: FETCH_STUDENTANSWER, payload: studentAnswers });

      if (callback) callback();
    } catch (error) {
      console.error("Error fetching student answer:", error);
    }
  };

// fetch studentAnswer in this class
export const fetchClassroomStudentAnswers =
  (options, callback) => async (dispatch, getStore) => {
    const { classroomId, booksetId, bookTypeId, bookId, unitId } = options;

    // 使用 URLSearchParams 來處理查詢參數
    const queryParams = new URLSearchParams();

    if (booksetId) queryParams.append("booksetId", booksetId);
    if (bookTypeId) queryParams.append("bookTypeId", bookTypeId);
    if (bookId) queryParams.append("bookId", bookId);
    if (unitId) queryParams.append("unitId", unitId);

    // 構建查詢字串
    const query = queryParams.toString() ? `?${queryParams.toString()}` : "";

    try {
      // 發送請求到後端來獲取對應的資料;
      const response = await axios.get(
        `classroom/${classroomId}/studentAnswers${query}`
      );
      // 從 response.data 解構 studentAnswer
      const { studentAnswers } = response.data;
      // 假設你有一個 action type 來處理返回的數據
      dispatch({ type: FETCH_CLASSROOMSTUDENTANSWER, payload: studentAnswers });
      if (callback) callback();
    } catch (error) {
      console.error("Error fetching student answer:", error);
    }
  };

//delete studentAnswer

export const deleteStudentAnswer =
  (fromProps, callback) => async (dispatch, getStore) => {
    const { unit: unitId, user: userId } = fromProps; // 解構縮短

    try {
      // **1. 先查找 studentAnswer**
      const { data } = await axios.get(`studentAnswer/${unitId}/${userId}`);
      const studentAnswer = data.studentAnswer || null;

      // **2. 刪除音檔**
      if (studentAnswer) {
        // **收集所有需要刪除的音檔 ID**
        const audioIds = studentAnswer.sections.flatMap((section) =>
          section.data.flatMap(
            (page) =>
              (page.answers?.audios || [])
                .map((audio) => audio?._audio)
                .filter(Boolean) // 過濾掉 null/undefined
          )
        );

        // **3. 刪除音檔**
        if (audioIds.length > 0) {
          await Promise.all(
            audioIds.map((audioId) =>
              axios
                .delete(`student/audio/${audioId}`)
                .catch((err) => console.error(`刪除音檔失敗: ${audioId}`, err))
            )
          );
          console.log(`成功刪除 ${audioIds.length} 個音檔`);
        } else {
          console.log("沒有音檔需要刪除");
        }
      } else {
        console.log("未找到對應的 studentAnswer，無需刪除音檔");
      }
      // **4. 刪除studentanswer**
      await axios.delete(`studentAnswer`, { params: { unitId, userId } });

      dispatch({
        type: DELETE_STUDENTANSWER,
        payload: { unitId, userId }
      });

      if (callback) callback();
    } catch (error) {
      console.error("刪除資料失敗", error);
    }
  };

/**
 * Record wrong answers with full context for adaptive learning
 * @param {Array} wrongAnswers - Array of wrong answer objects with context
 * @param {Function} callback - Optional callback function
 *
 * Example wrongAnswer object:
 * {
 *   userId: "user_id",
 *   booksetId: "bookset_id",
 *   booktypeId: "booktype_id",
 *   bookId: "book_id",
 *   unitId: "unit_id",
 *   sectionId: "section_id",
 *   pageId: "page_id",
 *   itemType: "word", // or "sentence", "question", "phrase"
 *   correctText: "cat",
 *   studentText: "dog",
 *   questionStyle: 10,
 *   questionStyleType: "reading"
 * }
 */
export const recordWrongAnswers = (wrongAnswers, callback) => async () => {
  if (!wrongAnswers || wrongAnswers.length === 0) {
    if (callback) callback([]);
    return;
  }

  try {
    const apolloClient = initApollo();

    // Use batch mutation to record all wrong answers in a single API call
    const result = await apolloClient.mutate({
      mutation: RECORD_WRONG_ANSWERS_BATCH,
      variables: {
        inputs: wrongAnswers
      }
    });

    if (callback) callback(result);
  } catch (error) {
    console.error("Failed to record wrong answers:", error);
  }
};

/**
 * Record correct answers for previously wrong items (during practice/redo)
 * Increments timesCorrect counter and may mark item as mastered based on threshold
 * @param {Array} correctAnswers - Array of correct answer objects with wrong item IDs
 * @param {Function} callback - Optional callback function
 *
 * Example correctAnswer object:
 * {
 *   wrongItemId: "wrong_answer_item_id", // ID of the WrongAnswerItem to update
 *   userId: "user_id",
 *   correctText: "cat" // The correct answer that was answered correctly
 * }
 */
export const recordCorrectAnswers = (correctAnswers, callback) => async () => {
  if (!correctAnswers || correctAnswers.length === 0) {
    if (callback) callback([]);
    return;
  }

  try {
    const apolloClient = initApollo();

    // Use batch mutation to record all correct answers in a single API call
    const result = await apolloClient.mutate({
      mutation: RECORD_CORRECT_ANSWERS_BATCH,
      variables: {
        inputs: correctAnswers
      }
    });

    if (callback) callback(result);
  } catch (error) {
    console.error("Failed to record correct answers:", error);
  }
};
